import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import { globalStyles, colors, fonts, CATEGORIES } from './lib/styles';
import { GridBackground, Logo, NeonBadge, CategoryPill, Spinner } from './components/UI';
import AuthPage from './components/AuthPage';
import ListingCard from './components/ListingCard';
import ListingDetail from './components/ListingDetail';
import PostListing from './components/PostListing';
import MessagesPage from './components/MessagesPage';

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('home'); // 'home' | 'messages'
  const [selectedListing, setSelectedListing] = useState(null);
  const [showPost, setShowPost] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  // Auth init
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) handleUserLogin(session.user);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) handleUserLogin(session.user);
      else { setUser(null); setProfile(null); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleUserLogin = async (u) => {
    setUser(u);
    const { data } = await supabase.from('profiles').select('*').eq('id', u.id).single();
    setProfile(data);
    setLoading(false);
    setShowAuth(false);
  };

  // Load listings
  useEffect(() => { loadListings(); }, []);

  const loadListings = async () => {
    const { data } = await supabase
      .from('listings')
      .select('*, profiles(handle), bids(id, amount, note, user_id, profiles(handle))')
      .order('created_at', { ascending: false });
    if (data) setListings(data);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null); setPage('home');
  };

  const refreshListing = async (id) => {
    const { data } = await supabase
      .from('listings')
      .select('*, profiles(handle), bids(id, amount, note, user_id, profiles(handle))')
      .eq('id', id)
      .single();
    if (data) {
      setListings(ls => ls.map(l => l.id === id ? data : l));
      setSelectedListing(data);
    }
  };

  const filtered = listings.filter(l => {
    const matchCat = activeCategory === 'All' || l.category === activeCategory;
    const matchSearch = !search || l.title.toLowerCase().includes(search.toLowerCase()) ||
      l.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{globalStyles}</style>
        <Spinner size={32} />
      </div>
    );
  }

  if (showAuth && !user) {
    return <><style>{globalStyles}</style><AuthPage onAuth={handleUserLogin} /></>;
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, color: colors.text, fontFamily: fonts.mono, position: 'relative' }}>
      <style>{globalStyles}</style>
      <GridBackground />

      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        borderBottom: `1px solid ${colors.border}`,
        background: 'rgba(4,8,13,0.92)', backdropFilter: 'blur(10px)',
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <button onClick={() => setPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <Logo />
            </button>
            <div style={{ width: '1px', height: '28px', background: colors.border }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.cyan, boxShadow: `0 0 8px ${colors.cyan}`, animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: '10px', color: '#00ffc870', letterSpacing: '0.1em' }}>{listings.length} ACTIVE</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {user ? (
              <>
                <button onClick={() => setPage('messages')} style={{
                  background: page === 'messages' ? 'rgba(0,255,200,0.08)' : 'transparent',
                  border: `1px solid ${page === 'messages' ? colors.cyanDim : colors.border}`,
                  color: page === 'messages' ? colors.cyan : colors.textDim,
                  borderRadius: '3px', padding: '7px 14px', cursor: 'pointer',
                  fontFamily: fonts.mono, fontSize: '11px', letterSpacing: '0.08em',
                  transition: 'all 0.2s',
                }}>MESSAGES</button>
                <span style={{ fontFamily: fonts.display, fontSize: '11px', color: '#00ffc860' }}>
                  {profile?.handle || user.email?.split('@')[0]}
                </span>
                <button onClick={handleSignOut} style={{
                  background: 'transparent', border: `1px solid ${colors.border}`, color: colors.textDim,
                  borderRadius: '3px', padding: '7px 12px', cursor: 'pointer',
                  fontFamily: fonts.mono, fontSize: '10px', letterSpacing: '0.08em',
                }}>SIGN OUT</button>
              </>
            ) : (
              <button onClick={() => setShowAuth(true)} style={{
                background: 'transparent', border: `1px solid ${colors.cyanDim}`, color: colors.cyan,
                borderRadius: '3px', padding: '7px 14px', cursor: 'pointer',
                fontFamily: fonts.display, fontSize: '11px', letterSpacing: '0.1em', fontWeight: 700,
              }}>LOG IN</button>
            )}

            <button
              onClick={() => user ? setShowPost(true) : setShowAuth(true)}
              style={{
                fontFamily: fonts.display, fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
                padding: '8px 18px', borderRadius: '3px', cursor: 'pointer',
                background: colors.cyan, color: '#000', border: `1px solid ${colors.cyan}`,
                boxShadow: `0 0 14px ${colors.cyanDim}`, transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 24px ${colors.cyanGlow}`; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 0 14px ${colors.cyanDim}`; }}
            >
              + POST SHIFT
            </button>
          </div>
        </div>
      </header>

      {/* Messages page */}
      {page === 'messages' && user && (
        <div style={{ position: 'relative', zIndex: 2 }}>
          <MessagesPage currentUser={user} />
        </div>
      )}

      {/* Home page */}
      {page === 'home' && (
        <>
          {/* Hero */}
          <div style={{
            position: 'relative', zIndex: 2, textAlign: 'center',
            padding: '56px 24px 40px',
            borderBottom: `1px solid ${colors.border}`,
          }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(0,255,200,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />
            <p style={{ fontFamily: fonts.mono, fontSize: '10px', letterSpacing: '0.3em', color: '#00ffc860', marginBottom: '14px', textTransform: 'uppercase' }}>
              NODE.NETWORK.4E // JOB EXCHANGE PROTOCOL
            </p>
            <h1 style={{
              fontFamily: fonts.display, fontWeight: 900,
              fontSize: 'clamp(26px, 4.5vw, 48px)',
              color: colors.text, margin: '0 0 14px',
              letterSpacing: '0.05em', lineHeight: 1.15,
              textShadow: '0 0 40px rgba(0,255,200,0.15)',
            }}>
              A NEW WAY TO WORK.<br />
              <span style={{ color: colors.cyan, textShadow: `0 0 30px ${colors.cyanGlow}` }}>A NEW WAY TO LIVE.</span>
            </h1>
            <p style={{ fontFamily: fonts.mono, fontSize: '13px', color: colors.textDim, maxWidth: '500px', margin: '0 auto 28px', lineHeight: 1.7 }}>
              Post any job. Bid on any shift. From tech contracts to odd jobs — connect with operators across the grid.
            </p>
            <div style={{ maxWidth: '500px', margin: '0 auto', position: 'relative' }}>
              <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: colors.cyanGlow, fontSize: '16px' }}>⌕</span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="SEARCH THE GRID..."
                style={{
                  width: '100%', padding: '11px 16px 11px 40px',
                  borderRadius: '3px', background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${colors.cyanDim}`, color: colors.text,
                  fontFamily: fonts.mono, fontSize: '12px', letterSpacing: '0.06em',
                  outline: 'none', boxSizing: 'border-box',
                  boxShadow: '0 0 20px rgba(0,255,200,0.04)',
                }} />
            </div>
          </div>

          {/* Main */}
          <main style={{ position: 'relative', zIndex: 2, maxWidth: '1100px', margin: '0 auto', padding: '28px 24px 80px' }}>
            {/* Categories */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {CATEGORIES.map(c => (
                <CategoryPill key={c} label={c} active={activeCategory === c} onClick={() => setActiveCategory(c)} />
              ))}
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', fontFamily: fonts.mono, fontSize: '11px', color: colors.textFaint, letterSpacing: '0.08em' }}>
              <span>{filtered.length} LISTINGS</span>
              <span>·</span>
              <span>{filtered.filter(l => l.pricing_type === 'bid').length} OPEN BIDS</span>
              <span>·</span>
              <span>{filtered.filter(l => l.urgent).length} URGENT</span>
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontFamily: fonts.display, fontSize: '13px', color: colors.textFaint, letterSpacing: '0.1em' }}>NO SIGNALS FOUND</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '12px' }}>
                {filtered.map(l => (
                  <ListingCard key={l.id} listing={l} onClick={setSelectedListing} />
                ))}
              </div>
            )}
          </main>

          <footer style={{
            position: 'relative', zIndex: 2,
            borderTop: `1px solid ${colors.border}`, padding: '20px 24px', textAlign: 'center',
          }}>
            <div style={{ fontFamily: fonts.display, fontSize: '10px', color: '#ffffff12', letterSpacing: '0.2em' }}>
              FERRUM_SHIFT // NODE v1.0 // ALL SHIFTS ENCRYPTED // GRID ONLINE
            </div>
          </footer>
        </>
      )}

      {/* Modals */}
      {selectedListing && (
        <ListingDetail
          listing={selectedListing}
          currentUser={user}
          onClose={() => setSelectedListing(null)}
          onBidPlaced={() => refreshListing(selectedListing.id)}
        />
      )}
      {showPost && (
        <PostListing
          currentUser={user}
          onClose={() => setShowPost(false)}
          onPosted={loadListings}
        />
      )}
    </div>
  );
}
